import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getStMissedVisitForm,
  updateStMissedVisitForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  CalendarIcon,
  CommentsIcon,
  DeleteIcon,
  FileCheckIcon,
  PhoneIcon,
  PlusIcon,
  ShieldIcon,
  TargetIcon,
  BellIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  ST_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ATTESTATION_TEXT,
  OUTREACH_METHOD_OPTIONS,
  PRIMARY_REASON_OPTIONS,
  YES_NO_OPTIONS,
  emptyOutreachAttempt,
  emptyStMissedVisitPayload,
  normalizeOutreachAttempts,
  today,
} from "./stMissedVisitContent";
import {
  TIME_OPTIONS_15,
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "../ptNotes/ptVisitCommon";
import StNotesCommonHeader from "./StNotesCommonHeader";
import {
  normalizeStCommonHeaderState,
  resolveStNotesCommonHeader,
  withNormalizedStCommonHeader,
} from "./stNotesCommonHeaderUtils";

const DATE_MAX = new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyStMissedVisitPayload(),
  missedVisitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const toBool = (value) => value === true || value === "true" || value === "Yes";

const ReasonPills = ({ value, canEdit, onChange }) => (
  <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Primary reason">
    {PRIMARY_REASON_OPTIONS.map((opt) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          type="button"
          disabled={!canEdit}
          aria-pressed={selected}
          onClick={() => onChange(selected ? "" : opt)}
          className={`h-8 px-2.5 rounded-md border text-[11px] font-semibold whitespace-nowrap transition-colors ${
            selected
              ? "border-indigo-600 bg-indigo-600 text-white"
              : "border-slate-200 bg-slate-50 text-indigo-800 hover:border-indigo-300"
          } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

ReasonPills.propTypes = {
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const YesNoSegment = ({ name, value, canEdit, onChange }) => (
  <div
    className="inline-flex overflow-hidden rounded-md border border-slate-200 shrink-0"
    role="radiogroup"
    aria-label={name}
  >
    {YES_NO_OPTIONS.map((opt, idx) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          type="button"
          disabled={!canEdit}
          aria-pressed={selected}
          onClick={() => onChange(selected ? "" : opt)}
          className={`h-8 min-w-[3.25rem] px-3 text-[12px] font-semibold transition-colors ${
            idx === 0 ? "border-r border-slate-200" : ""
          } ${
            selected
              ? "bg-indigo-600 text-white"
              : "bg-white text-slate-700 hover:bg-indigo-50"
          } ${!canEdit ? "cursor-default opacity-80" : "cursor-pointer"}`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

YesNoSegment.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const DateTimeField = ({
  dateName,
  timeName,
  dateValue,
  timeValue,
  canEdit,
  printMode,
  onDateChange,
  onTimeChange,
  dateMax,
}) => (
  <div className="grid grid-cols-2 gap-1.5">
    {printMode ? (
      <>
        <div className={`${fieldClass} flex items-center`}>
          {displayDateUS(dateValue) || "—"}
        </div>
        <div className={`${fieldClass} flex items-center`}>{timeValue || "—"}</div>
      </>
    ) : (
      <>
        <InputDatePicker
          name={dateName}
          formatSeparator="/"
          allowManualEntry
          disabled={!canEdit}
          value={formatDateForInput(dateValue) || ""}
          onChange={(event) => onDateChange(event.target.value)}
          max={dateMax}
          className="w-full"
          inputClassName={`${fieldClass} bg-white`}
        />
        <select
          name={timeName}
          className={fieldClass}
          value={timeValue || ""}
          disabled={!canEdit}
          onChange={(e) => onTimeChange(e.target.value)}
        >
          <option value="">--:-- --</option>
          {TIME_OPTIONS_15.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </>
    )}
  </div>
);

DateTimeField.propTypes = {
  dateName: PropTypes.string.isRequired,
  timeName: PropTypes.string.isRequired,
  dateValue: PropTypes.string,
  timeValue: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onDateChange: PropTypes.func.isRequired,
  onTimeChange: PropTypes.func.isRequired,
  dateMax: PropTypes.string,
};

const StMissedVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyStMissedVisitPayload();
      const loadedPayload =
        data.payload &&
        typeof data.payload === "object" &&
        !Array.isArray(data.payload)
          ? data.payload
          : {};

      setValues({
        ...base,
        ...loadedPayload,
        ...pickPtVisitMeta(loadedPayload, data),
        missedVisitDate:
          data.missedVisitDate || loadedPayload.missedVisitDate || today(),
        commonHeader: normalizeStCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        outreachAttempts: normalizeOutreachAttempts(
          loadedPayload.outreachAttempts,
        ),
        physicianNotified: toBool(loadedPayload.physicianNotified),
        familyNotified: toBool(loadedPayload.familyNotified),
        caseManagerNotified: toBool(loadedPayload.caseManagerNotified),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      });
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveStNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Speech Therapy Missed Visit",
            formType:
              clinicalForm.formType || draft?.formType || "st_missed_visit",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            missedVisitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyStMissedVisitPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getStMissedVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveStNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: { ...existingPayload, commonHeader },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const updateAttempt = (id, patch) => {
    setValues((prev) => ({
      ...prev,
      outreachAttempts: (prev.outreachAttempts || []).map((row) =>
        row.id === id ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addAttempt = () => {
    setValues((prev) => ({
      ...prev,
      outreachAttempts: [
        ...(prev.outreachAttempts || []),
        emptyOutreachAttempt((prev.outreachAttempts || []).length + 1),
      ],
    }));
  };

  const removeAttempt = (id) => {
    setValues((prev) => {
      const next = (prev.outreachAttempts || []).filter((row) => row.id !== id);
      return {
        ...prev,
        outreachAttempts: next.length ? next : [emptyOutreachAttempt(1)],
      };
    });
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        ST_FORM_EDITOR_ROLES,
        "View only — only ST can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const { caregiverSlot, filledByUserId, assertCaregiverForCreate } =
    useFormCaregiverPicker({
      patientId,
      formType:
        form?.formType ||
        draft?.formType ||
        clinicalForm?.formType ||
        "st_missed_visit",
      role,
      canEdit,
      preferredStaffId: draft?.visit?.staffId ?? null,
      currentFilledByUserId: form?.filledBy || clinicalForm?.filledBy || null,
    });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      missedVisitDate,
      ...stFields
    } = values;

    const nestedPayload = withNormalizedStCommonHeader({
      ...stFields,
      missedVisitDate: missedVisitDate
        ? formatDateForInput(missedVisitDate)
        : "",
      rescheduledDate: values.rescheduledDate
        ? formatDateForInput(values.rescheduledDate)
        : "",
      physicianNotifiedDate: values.physicianNotifiedDate
        ? formatDateForInput(values.physicianNotifiedDate)
        : "",
      familyNotifiedDate: values.familyNotifiedDate
        ? formatDateForInput(values.familyNotifiedDate)
        : "",
      caseManagerNotifiedDate: values.caseManagerNotifiedDate
        ? formatDateForInput(values.caseManagerNotifiedDate)
        : "",
      outreachAttempts: (values.outreachAttempts || []).map((row) => ({
        id: row.id,
        date: row.date ? formatDateForInput(row.date) : "",
        time: row.time || "",
        method: row.method || "",
        outcome: row.outcome || "",
      })),
      physicianNotified: Boolean(values.physicianNotified),
      familyNotified: Boolean(values.familyNotified),
      caseManagerNotified: Boolean(values.caseManagerNotified),
    });

    const payload = {
      flag,
      missedVisitDate: missedVisitDate
        ? formatDateForInput(missedVisitDate)
        : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag, {
      requireTimes: false,
    });
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateStMissedVisitForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.missedVisitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showReasonOther = values.primaryReason === "Other";

  const notificationBoxes = [
    {
      key: "physician",
      label: "Physician Notified",
      checked: values.physicianNotified,
      date: values.physicianNotifiedDate,
      time: values.physicianNotifiedTime,
      checkedKey: "physicianNotified",
      dateKey: "physicianNotifiedDate",
      timeKey: "physicianNotifiedTime",
    },
    {
      key: "family",
      label: "Family/Caregiver Notified",
      checked: values.familyNotified,
      date: values.familyNotifiedDate,
      time: values.familyNotifiedTime,
      checkedKey: "familyNotified",
      dateKey: "familyNotifiedDate",
      timeKey: "familyNotifiedTime",
    },
    {
      key: "caseManager",
      label: "Case Manager Notified",
      checked: values.caseManagerNotified,
      date: values.caseManagerNotifiedDate,
      time: values.caseManagerNotifiedTime,
      checkedKey: "caseManagerNotified",
      dateKey: "caseManagerNotifiedDate",
      timeKey: "caseManagerNotifiedTime",
    },
  ];

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            {form.formName || "Speech Therapy Missed Visit"}
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">Missed Visit Report</p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Missed Visit Date"
          dateValue={displayDateUS(values.missedVisitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400 leading-none">
                  Missed Visit Date
                </div>
                <InputDatePicker
                  name="missedVisitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.missedVisitDate) || ""}
                  onChange={(event) =>
                    setValue("missedVisitDate", event.target.value)
                  }
                  max={DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <StNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={AlertCircleIcon} title="7. Primary Reason for Missed Visit">
          <ReasonPills
            value={values.primaryReason || ""}
            canEdit={canEdit}
            onChange={(next) => {
              setValues((prev) => ({
                ...prev,
                primaryReason: next,
                reasonOtherSpecify:
                  next === "Other" ? prev.reasonOtherSpecify : "",
              }));
            }}
          />
          {showReasonOther ? (
            <div className="mt-2">
              <Field label="Other — Specify">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.reasonOtherSpecify || ""}
                  disabled={!canEdit}
                  placeholder="Specify other reason…"
                  onChange={(e) =>
                    setValue("reasonOtherSpecify", e.target.value)
                  }
                />
              </Field>
            </div>
          ) : null}
          <div className="mt-2">
            <Field label="Detailed Narrative (Clinical Rationale)">
              <textarea
                rows={4}
                className={`${textareaClass} min-h-[5.5rem]`}
                value={values.detailedNarrative || ""}
                disabled={!canEdit}
                placeholder="Provide specific details regarding the missed visit…"
                onChange={(e) => setValue("detailedNarrative", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={PhoneIcon} title="8. Outreach & Communication Logs">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full min-w-[640px] text-left">
              <thead>
                <tr className="bg-indigo-50/70 text-[9px] font-semibold uppercase tracking-wide text-indigo-700">
                  <th className="px-2 py-1.5 w-16">Attempt #</th>
                  <th className="px-2 py-1.5 w-[28%]">Date / Time</th>
                  <th className="px-2 py-1.5 w-[22%]">Method</th>
                  <th className="px-2 py-1.5">Outcome</th>
                  {canEdit ? (
                    <th className="px-2 py-1.5 w-12 text-right">Action</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {(values.outreachAttempts || []).map((row, idx) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 text-[12px] font-semibold text-slate-700">
                      {idx + 1}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      <DateTimeField
                        dateName={`outreachDate-${row.id}`}
                        timeName={`outreachTime-${row.id}`}
                        dateValue={row.date}
                        timeValue={row.time}
                        canEdit={canEdit}
                        printMode={printMode}
                        onDateChange={(v) => updateAttempt(row.id, { date: v })}
                        onTimeChange={(v) => updateAttempt(row.id, { time: v })}
                        dateMax={DATE_MAX}
                      />
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {printMode ? (
                        <div className={`${fieldClass} flex items-center`}>
                          {row.method || "—"}
                        </div>
                      ) : (
                        <select
                          className={fieldClass}
                          value={row.method || ""}
                          disabled={!canEdit}
                          onChange={(e) =>
                            updateAttempt(row.id, { method: e.target.value })
                          }
                        >
                          <option value="">Select…</option>
                          {OUTREACH_METHOD_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {printMode ? (
                        <div className={`${fieldClass} flex items-center`}>
                          {row.outcome || "—"}
                        </div>
                      ) : (
                        <input
                          type="text"
                          className={fieldClass}
                          value={row.outcome || ""}
                          disabled={!canEdit}
                          placeholder="e.g., No answer, voicemail left"
                          onChange={(e) =>
                            updateAttempt(row.id, { outcome: e.target.value })
                          }
                        />
                      )}
                    </td>
                    {canEdit ? (
                      <td className="px-2 py-1.5 align-middle text-right">
                        <button
                          type="button"
                          className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50"
                          aria-label="Remove attempt"
                          onClick={() => removeAttempt(row.id)}
                        >
                          <DeleteIcon className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {canEdit ? (
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-700"
              onClick={addAttempt}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add Outreach Attempt
            </button>
          ) : null}
        </Section>

        <Section icon={TargetIcon} title="9. Plan of Care Analysis">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-stretch">
            <Field
              label="ST Interventions / Goals Affected"
              span="flex flex-col h-full min-h-[7rem]"
            >
              <textarea
                rows={5}
                className={`${textareaClass} flex-1 min-h-[7rem]`}
                value={values.interventionsAffected || ""}
                disabled={!canEdit}
                placeholder="List specific speech, swallowing, or cognitive goals impacted by this absence…"
                onChange={(e) =>
                  setValue("interventionsAffected", e.target.value)
                }
              />
            </Field>
            <Field
              label="Functional Impact on ST Plan of Care"
              span="flex flex-col h-full min-h-[7rem]"
            >
              <textarea
                rows={5}
                className={`${textareaClass} flex-1 min-h-[7rem]`}
                value={values.functionalImpact || ""}
                disabled={!canEdit}
                placeholder="Describe the regression risk or delay in functional communication/safety…"
                onChange={(e) => setValue("functionalImpact", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={ShieldIcon} title="10. Safety & Continuity Validation">
          <div className="divide-y divide-slate-100">
            <div className="flex flex-wrap items-center justify-between gap-2 py-2">
              <p className="text-[12px] text-slate-800">
                Patient is currently safe in the home environment?
              </p>
              <YesNoSegment
                name="patientSafeAtHome"
                value={values.patientSafeAtHome || ""}
                canEdit={canEdit}
                onChange={(v) => setValue("patientSafeAtHome", v)}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 py-2">
              <p className="text-[12px] text-slate-800">
                Missed visit exceeds agency&apos;s frequency tolerance?
              </p>
              <YesNoSegment
                name="exceedsFrequencyTolerance"
                value={values.exceedsFrequencyTolerance || ""}
                canEdit={canEdit}
                onChange={(v) => setValue("exceedsFrequencyTolerance", v)}
              />
            </div>
          </div>
        </Section>

        <Section icon={BellIcon} title="11. Required Notifications">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 items-stretch">
            {notificationBoxes.map((box) => (
              <div
                key={box.key}
                className="h-full rounded-md border border-slate-200 bg-slate-50/40 p-2.5 flex flex-col gap-2"
              >
                <label
                  className={`inline-flex items-center gap-2 text-[12px] font-semibold text-slate-800 ${
                    canEdit ? "cursor-pointer" : "cursor-default"
                  }`}
                >
                  <input
                    type="checkbox"
                    className={checkboxClass}
                    checked={Boolean(box.checked)}
                    disabled={!canEdit}
                    onChange={(e) => setValue(box.checkedKey, e.target.checked)}
                  />
                  {box.label}
                </label>
                <DateTimeField
                  dateName={`${box.key}Date`}
                  timeName={`${box.key}Time`}
                  dateValue={box.date}
                  timeValue={box.time}
                  canEdit={canEdit}
                  printMode={printMode}
                  onDateChange={(v) => setValue(box.dateKey, v)}
                  onTimeChange={(v) => setValue(box.timeKey, v)}
                  dateMax={DATE_MAX}
                />
              </div>
            ))}
          </div>
        </Section>

        <Section icon={CalendarIcon} title="12. Rescheduling & Catch-up Strategy">
          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,0.32fr)_minmax(0,0.68fr)] gap-3 items-end">
            <Field label="Rescheduled Date">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {displayDateUS(values.rescheduledDate) || "—"}
                </div>
              ) : (
                <InputDatePicker
                  name="rescheduledDate"
                  formatSeparator="/"
                  allowManualEntry
                  disabled={!canEdit}
                  value={formatDateForInput(values.rescheduledDate) || ""}
                  onChange={(event) =>
                    setValue("rescheduledDate", event.target.value)
                  }
                  className="w-full"
                  inputClassName={`${fieldClass} bg-white`}
                />
              )}
            </Field>
            <Field label="Frequency Adjustment (if any)">
              {printMode ? (
                <div className={`${fieldClass} flex items-center`}>
                  {values.frequencyAdjustment || "—"}
                </div>
              ) : (
                <input
                  type="text"
                  className={fieldClass}
                  value={values.frequencyAdjustment || ""}
                  disabled={!canEdit}
                  placeholder="e.g., Added visit on Week 4 to maintain 2w10 POC"
                  onChange={(e) =>
                    setValue("frequencyAdjustment", e.target.value)
                  }
                />
              )}
            </Field>
          </div>
        </Section>

        <Section icon={CommentsIcon} title="13. Additional Therapist Comments">
          <textarea
            rows={4}
            className={`${textareaClass} min-h-[5.5rem]`}
            value={values.additionalNotes || ""}
            disabled={!canEdit}
            placeholder="Enter any extra-clinical notes here…"
            onChange={(e) => setValue("additionalNotes", e.target.value)}
          />
        </Section>

        <Section icon={FileCheckIcon} title="14. Professional Attestation">
          <div className="rounded-md border border-indigo-200 bg-indigo-50/30 px-3 py-2.5">
            <p className="text-[12px] leading-relaxed text-slate-700">
              {ATTESTATION_TEXT}
            </p>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          canEdit={canEdit}
          canSign={canSign}
          printMode={printMode}
          values={values}
          onPatientSign={signatures.handlePatientSign}
          onClinicianSign={handleClinicianSign}
          onChange={setValues}
          clinicianLabel="Clinician Digital Signature"
          clinicianSectionTitle="Clinician Digital Signature"
        />

        {isReviewer ? (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        ) : values.reviewComments ? (
          <OfficeCommentsNote comments={values.reviewComments} />
        ) : null}

        {!printMode &&
          (permissions.canSendToOffice ||
            permissions.canToBeSign ||
            permissions.canReview ||
            permissions.canEdit) && (
            <FormActionBar
              permissions={permissions}
              status={form.status}
              canEdit={canEdit}
              saving={saving}
              onSubmit={submit}
            />
          )}
      </div>
    </div>
  );
};

StMissedVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default StMissedVisitForm;
